"use client";

import Image from "next/image";
import Link from "next/link";
import {
  AlertTriangle,
  ChevronDown,
  Download,
  ImagePlus,
  LoaderCircle,
  RotateCcw,
  Trash2,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogWizardBody,
  DialogWizardFooter,
  DialogWizardLayout,
  DialogWizardPanel,
} from "@/components/ui/dialog";
import {
  BROWSER_IMAGE_ACCEPT,
  BrowserImagePreparationError,
  prepareBrowserImageUpload,
} from "@/lib/create/browserImageUpload";
import { downloadTryOnWithWatermark } from "@/lib/create/downloadTryOn";
import {
  VIRTUAL_TRY_ON_CONSENT_VERSION,
  VIRTUAL_TRY_ON_PRIVACY_PATH,
  VIRTUAL_TRY_ON_TERMS_PATH,
} from "@/constants/virtualTryOnConsent";

const TRY_ON_WEARER_MAX_INPUT_BYTES = 20 * 1024 * 1024;
const TRY_ON_WEARER_MAX_OUTPUT_BYTES = 4_000_000;

export interface TryOnWorkspaceStatus {
  status: TryOnStatus;
  error: string | null;
}

type TryOnStatus = "idle" | "generating" | "complete" | "error";

interface VirtualTryOnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  frontImageUrl: string;
  frontImagePath?: string | null;
  backImageUrl?: string | null;
  backImagePath?: string | null;
  ticketId: string | null;
  unavailable?: boolean;
  onPendingChange?: (pending: boolean) => void;
  onWorkspaceStatusChange?: (status: TryOnWorkspaceStatus) => void;
}

interface TryOnErrorResponse {
  error?: unknown;
  message?: unknown;
}

function responseError(payload: TryOnErrorResponse | null): string {
  if (typeof payload?.error === "string" && payload.error.trim()) {
    return payload.error;
  }
  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message;
  }
  return "We couldn’t create this try-on. Please try again.";
}

function wearerPhotoError(error: unknown): string {
  if (!(error instanceof BrowserImagePreparationError)) {
    return "We couldn’t read that photo. Try another JPG, PNG, or WebP image.";
  }
  switch (error.code) {
    case "unsupported_format":
      return "Choose a JPG, PNG, or WebP photo.";
    case "input_too_large":
      return "Choose an original photo no larger than 20 MB.";
    case "output_too_large":
      return "We couldn’t make that photo small enough to upload. Try another photo.";
    case "unreadable_image":
      return "We couldn’t read that photo. Try another JPG, PNG, or WebP image.";
  }
}

export default function VirtualTryOnDialog({
  open,
  onOpenChange,
  frontImageUrl,
  frontImagePath,
  backImageUrl,
  backImagePath,
  ticketId,
  unavailable = false,
  onPendingChange,
  onWorkspaceStatusChange,
}: VirtualTryOnDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const consentInputRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef<AbortController | null>(null);
  const photoPreparationIdRef = useRef(0);
  const [wearerPhoto, setWearerPhoto] = useState<File | null>(null);
  const [wearerPreviewUrl, setWearerPreviewUrl] = useState<string | null>(null);
  const [photoPreparing, setPhotoPreparing] = useState(false);
  const [status, setStatus] = useState<TryOnStatus>("idle");
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadPending, setDownloadPending] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  const reset = useCallback(() => {
    photoPreparationIdRef.current += 1;
    requestRef.current?.abort();
    requestRef.current = null;
    setWearerPhoto(null);
    setWearerPreviewUrl(null);
    setPhotoPreparing(false);
    setStatus("idle");
    setResultUrl(null);
    setError(null);
    setDownloadPending(false);
    setConsentAccepted(false);
    setConsentError(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  useEffect(() => {
    return () => {
      if (wearerPreviewUrl) URL.revokeObjectURL(wearerPreviewUrl);
    };
  }, [wearerPreviewUrl]);

  useEffect(() => {
    return () => {
      if (resultUrl) URL.revokeObjectURL(resultUrl);
    };
  }, [resultUrl]);

  useEffect(() => {
    onPendingChange?.(status === "generating" || photoPreparing);
    return () => onPendingChange?.(false);
  }, [onPendingChange, photoPreparing, status]);

  useEffect(() => {
    onWorkspaceStatusChange?.({ status, error });
  }, [status, error, onWorkspaceStatusChange]);
  useEffect(
    () => () => onWorkspaceStatusChange?.({ status: "idle", error: null }),
    [onWorkspaceStatusChange]
  );

  useEffect(
    () => () => {
      photoPreparationIdRef.current += 1;
      requestRef.current?.abort();
    },
    []
  );

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) return;

    const preparationId = ++photoPreparationIdRef.current;
    setResultUrl(null);
    setStatus("idle");
    setError(null);
    setConsentAccepted(false);
    setConsentError(null);
    setPhotoPreparing(true);

    try {
      const preparedFile = await prepareBrowserImageUpload(file, {
        maxInputBytes: TRY_ON_WEARER_MAX_INPUT_BYTES,
        maxOutputBytes: TRY_ON_WEARER_MAX_OUTPUT_BYTES,
        maxWidth: 2048,
        maxHeight: 2048,
        quality: 0.86,
        // compressImage treats this as a target with 10% headroom. A 3 MB
        // target leaves ample room below the exact 4,000,000-byte API ceiling.
        targetFileSizeMB: 3,
      });
      if (photoPreparationIdRef.current !== preparationId) return;
      setWearerPhoto(preparedFile);
      setWearerPreviewUrl(URL.createObjectURL(preparedFile));
    } catch (caughtError) {
      if (photoPreparationIdRef.current !== preparationId) return;
      setError(wearerPhotoError(caughtError));
    } finally {
      if (photoPreparationIdRef.current === preparationId) {
        setPhotoPreparing(false);
      }
    }
  };

  const handleRemovePhoto = () => {
    photoPreparationIdRef.current += 1;
    setWearerPhoto(null);
    setWearerPreviewUrl(null);
    setPhotoPreparing(false);
    setResultUrl(null);
    setStatus("idle");
    setError(null);
    setConsentAccepted(false);
    setConsentError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleGenerate = async () => {
    const baseReference = frontImagePath?.trim() || frontImageUrl.trim();
    const backReference = backImagePath?.trim() || backImageUrl?.trim() || "";
    if (
      !wearerPhoto ||
      !ticketId?.trim() ||
      !baseReference ||
      unavailable ||
      photoPreparing ||
      status === "generating"
    ) {
      return;
    }

    if (!consentAccepted) {
      setConsentError(
        "Accept the Terms of Service and Privacy Policy to continue."
      );
      consentInputRef.current?.focus();
      return;
    }

    const body = new FormData();
    body.append("wearer", wearerPhoto);
    body.append("ticket_id", ticketId.trim());
    body.append("base_image_path", baseReference);
    body.append("consent_accepted", "true");
    body.append("consent_version", VIRTUAL_TRY_ON_CONSENT_VERSION);
    if (backReference) body.append("back_image_path", backReference);

    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    setStatus("generating");
    setResultUrl(null);
    setError(null);

    try {
      const response = await fetch("/api/create/try-on", {
        method: "POST",
        body,
        signal: controller.signal,
        headers: { Accept: "image/jpeg" },
      });
      if (!response.ok) {
        const payload = (await response
          .json()
          .catch(() => null)) as TryOnErrorResponse | null;
        throw new Error(responseError(payload));
      }

      const contentType = response.headers
        .get("content-type")
        ?.split(";", 1)[0]
        ?.trim()
        .toLowerCase();
      if (contentType !== "image/jpeg") {
        throw new Error(
          "We couldn’t load your try-on image. Please try again."
        );
      }
      const result = await response.blob();
      if (result.size <= 0) {
        throw new Error(
          "We couldn’t load your try-on image. Please try again."
        );
      }

      if (requestRef.current !== controller || controller.signal.aborted)
        return;
      setResultUrl(URL.createObjectURL(result));
      setStatus("complete");
    } catch (caughtError) {
      if (requestRef.current !== controller || controller.signal.aborted)
        return;
      if (
        caughtError instanceof DOMException &&
        caughtError.name === "AbortError"
      ) {
        return;
      }
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "We couldn’t create this try-on. Please try again."
      );
      setStatus("error");
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  };

  const handleDownload = async () => {
    if (!resultUrl || downloadPending) return;

    setDownloadPending(true);
    setError(null);
    try {
      await downloadTryOnWithWatermark(resultUrl);
    } catch {
      setError("We couldn’t prepare the download. Please try again.");
    } finally {
      setDownloadPending(false);
    }
  };

  const generateDisabled =
    unavailable ||
    photoPreparing ||
    !wearerPhoto ||
    !consentAccepted ||
    !ticketId?.trim() ||
    !(frontImagePath?.trim() || frontImageUrl.trim()) ||
    status === "generating";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        variant="flow"
        title={null}
        closeButtonLabel="Close virtual try-on"
        className="designer-try-on-dialog h-auto! max-h-[calc(100dvh-1rem)]! rounded-t-3xl! border-border! bg-background! text-foreground sm:max-h-[90dvh]! sm:max-w-[480px]! sm:rounded-3xl!"
        overlayClassName="bg-overlay! backdrop-blur-none!"
        closeButtonVariant="dialog"
        closeButtonClassName="right-4 top-7! rounded-full! bg-transparent sm:right-5! sm:top-6!"
        closeIconClassName="size-5"
      >
        <DialogWizardLayout className="h-auto">
          <div
            aria-hidden="true"
            className="mx-auto mt-3 h-1 w-8 shrink-0 rounded-full bg-border sm:hidden"
          />
          <DialogHeader className="shrink-0 px-6 pb-4 pt-4 pr-16 text-left sm:px-7 sm:pt-6 sm:pr-16">
            <DialogTitle className="flex min-h-11 items-center text-xl leading-7">
              Try on your dress
            </DialogTitle>
            <DialogDescription className="sr-only">
              {resultUrl
                ? "Your virtual try-on is ready."
                : "Add a full-body photo to see this dress on you."}
            </DialogDescription>
          </DialogHeader>

          <DialogWizardPanel>
            <DialogWizardBody className="space-y-4 px-6 pt-0 sm:px-7 sm:pt-0">
              {resultUrl ? (
                <section aria-label="Your virtual try-on" className="space-y-3">
                  <div className="relative mx-auto aspect-[2/3] w-full max-w-[min(20rem,44dvh)] overflow-hidden rounded-2xl bg-muted">
                    <Image
                      src={resultUrl}
                      alt="Virtual try-on result"
                      fill
                      sizes="(max-width: 639px) 80vw, 320px"
                      unoptimized
                      className="object-contain"
                    />
                  </div>
                  <p className="text-center text-sm text-muted-foreground">
                    Your try-on is ready. Your saved design is unchanged.
                  </p>
                  <p className="text-center text-xs leading-[18px] text-muted-foreground">
                    AI visualization only. Fit and fabric may differ. We retain
                    this result for product-quality review.
                  </p>
                </section>
              ) : (
                <>
                  <section
                    aria-label="Dress references used"
                    className="flex min-h-[52px] items-center gap-3"
                  >
                    <div className="relative h-[50px] w-[34px] shrink-0 overflow-hidden rounded-lg bg-muted">
                      <Image
                        src={frontImageUrl}
                        alt="Front dress reference"
                        fill
                        sizes="34px"
                        quality={75}
                        className="object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium">
                        Your current dress
                      </h3>
                      <p className="mt-0.5 text-xs leading-[18px] text-muted-foreground">
                        {backImageUrl || backImagePath
                          ? "Front + back"
                          : "Front view"}
                        {" · Free try-on"}
                      </p>
                    </div>
                  </section>

                  {status === "generating" ? (
                    <section
                      role="status"
                      aria-live="polite"
                      className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-2xl bg-muted p-6 text-center"
                    >
                      <LoaderCircle
                        className="size-7 animate-spin text-primary motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                      <h3 className="text-base font-medium">
                        Creating your try-on…
                      </h3>
                      <p className="text-sm leading-5 text-muted-foreground">
                        You can return to your design while we finish.
                      </p>
                    </section>
                  ) : (
                    <section aria-label="Your photo" className="space-y-3">
                      <input
                        ref={inputRef}
                        id="virtual-try-on-wearer"
                        type="file"
                        accept={BROWSER_IMAGE_ACCEPT}
                        className="sr-only"
                        tabIndex={-1}
                        aria-label="Upload a full-body photo"
                        onChange={event => void handlePhotoChange(event)}
                      />

                      {wearerPhoto && wearerPreviewUrl ? (
                        <div className="flex items-center gap-4 rounded-2xl bg-muted p-4">
                          <div className="relative aspect-[2/3] w-20 shrink-0 overflow-hidden rounded-lg">
                            <Image
                              src={wearerPreviewUrl}
                              alt="Selected full-body photo"
                              fill
                              sizes="80px"
                              unoptimized
                              className="object-contain"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {wearerPhoto.name}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {photoPreparing
                                ? "Preparing your replacement photo…"
                                : "Ready for your try-on"}
                            </p>
                            <div className="mt-3 flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="min-h-11 rounded-xl border-border bg-background"
                                onClick={() => inputRef.current?.click()}
                                loading={photoPreparing}
                                loadingText="Replacing…"
                              >
                                Replace
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-11 rounded-xl"
                                onClick={handleRemovePhoto}
                                aria-label="Remove full-body photo"
                                title="Remove photo"
                              >
                                <Trash2 aria-hidden="true" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex w-full flex-col items-center gap-2.5 rounded-2xl bg-muted px-4 py-[18px] text-center">
                          {photoPreparing ? (
                            <LoaderCircle
                              className="size-6 animate-spin text-primary motion-reduce:animate-none"
                              aria-hidden="true"
                            />
                          ) : (
                            <ImagePlus
                              className="size-6 text-primary"
                              aria-hidden="true"
                            />
                          )}
                          <h3 className="text-[17px] font-medium leading-6">
                            {photoPreparing
                              ? "Preparing your photo…"
                              : "One clear photo of you"}
                          </h3>
                          <p className="text-[13px] leading-5 text-muted-foreground">
                            Head to toe, facing the camera.
                          </p>
                          <Button
                            variant="outline"
                            className="min-h-11 rounded-xl border-border bg-background px-5"
                            onClick={() => inputRef.current?.click()}
                            aria-describedby="virtual-try-on-photo-formats"
                          >
                            Choose a photo
                          </Button>
                        </div>
                      )}

                      <details className="group text-xs text-muted-foreground">
                        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
                          Photo tips & privacy
                          <ChevronDown
                            className="size-4 shrink-0 transition-transform group-open:rotate-180"
                            aria-hidden="true"
                          />
                        </summary>
                        <div className="space-y-2 pb-2 leading-[18px]">
                          <p id="virtual-try-on-photo-formats">
                            JPG, PNG, or WebP · originals up to 20 MB.
                          </p>
                          <p>
                            Use one clear, well-lit photo of one person facing
                            the camera. The AI matches your body, face, and
                            hair, preserving your hijab when shown.
                          </p>
                          <p id="virtual-try-on-consent-help">
                            Your source photo is sent securely for this try-on
                            and is not stored by Build-a-Dress. The generated
                            result is retained with your account for
                            product-quality review and removed when your account
                            is deleted.
                          </p>
                          <p>
                            <strong className="font-medium">
                              AI visualization only.
                            </strong>{" "}
                            Fit, fabric behavior, and final construction may
                            differ.
                          </p>
                        </div>
                      </details>
                    </section>
                  )}
                </>
              )}

              {error ? (
                <div
                  role="alert"
                  className="flex gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertTriangle
                    className="mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  <span>{error}</span>
                </div>
              ) : null}
            </DialogWizardBody>

            <DialogWizardFooter className="space-y-3.5 border-border bg-background px-6 pb-[max(1.75rem,env(safe-area-inset-bottom))] pt-4 sm:px-7 sm:pb-6 sm:pt-4 md:border-t md:pt-4">
              {resultUrl ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    variant="outline"
                    className="min-h-12 flex-1 rounded-xl border-border"
                    onClick={reset}
                    disabled={downloadPending}
                    leftIcon={<RotateCcw aria-hidden="true" />}
                  >
                    Try another photo
                  </Button>
                  <Button
                    className="designer-primary min-h-12 flex-1 rounded-xl"
                    onClick={() => void handleDownload()}
                    disabled={downloadPending}
                    loading={downloadPending}
                    loadingText="Preparing download…"
                    leftIcon={<Download aria-hidden="true" />}
                  >
                    Download try-on
                  </Button>
                </div>
              ) : status === "generating" ? (
                <Button
                  variant="outline"
                  className="min-h-12 w-full rounded-xl border-border"
                  onClick={() => handleOpenChange(false)}
                >
                  Back to design
                </Button>
              ) : (
                <>
                  <div className="flex items-start gap-2.5">
                    <input
                      ref={consentInputRef}
                      id="virtual-try-on-consent"
                      type="checkbox"
                      checked={consentAccepted}
                      required
                      aria-invalid={consentError ? "true" : undefined}
                      aria-describedby="virtual-try-on-consent-help virtual-try-on-consent-error"
                      className="mt-0.5 size-5 shrink-0 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onChange={event => {
                        setConsentAccepted(event.target.checked);
                        if (event.target.checked) setConsentError(null);
                      }}
                    />
                    <label
                      htmlFor="virtual-try-on-consent"
                      className="text-[13px] leading-[19px] text-foreground"
                    >
                      I agree to the{" "}
                      <Link
                        href={VIRTUAL_TRY_ON_TERMS_PATH}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2"
                      >
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link
                        href={VIRTUAL_TRY_ON_PRIVACY_PATH}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2"
                      >
                        Privacy Policy
                      </Link>
                      , and confirm I am the person shown or have their
                      permission.
                    </label>
                  </div>
                  {consentError ? (
                    <p
                      id="virtual-try-on-consent-error"
                      role="alert"
                      className="text-xs text-destructive"
                    >
                      {consentError}
                    </p>
                  ) : null}
                  <Button
                    className="designer-primary min-h-12 w-full rounded-xl"
                    onClick={() => void handleGenerate()}
                    disabled={generateDisabled}
                  >
                    Generate try-on
                  </Button>
                </>
              )}
            </DialogWizardFooter>
          </DialogWizardPanel>
        </DialogWizardLayout>
      </DialogContent>
    </Dialog>
  );
}
